import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../LangContext";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Activities() {
    const { t, lang } = useLang();
    const [items, setItems] = useState([]);

    useEffect(() => {
        axios.get(`${API}/activities`).then((r) => setItems(r.data)).catch(() => {});
    }, []);

    if (!items.length) return null;

    // Multilingual field picker with EN fallback (tolerates legacy plain strings)
    const pick = (f) => (f && typeof f === "object" ? (f[lang.toLowerCase()] || f.en || "") : f || "");

    return (
        <section id="activities" data-testid="activities-section" className="py-24 md:py-32 px-6 md:px-12 bg-ink">
            <div className="max-w-7xl mx-auto">
                <div className="mb-16 max-w-3xl">
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-volt">— {t.activities.label}</span>
                    <h2 className="font-graffiti text-4xl md:text-6xl tracking-tight mt-6 text-white leading-[0.95]">
                        {t.activities.title}
                    </h2>
                </div>

                <div className={`grid grid-cols-1 gap-px bg-white/10 border border-white/10 ${items.length === 1 ? "" : items.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                    {items.map((a, i) => {
                        const title = pick(a.title);
                        const description = pick(a.description);
                        const impact = pick(a.impact);
                        return (
                            <div
                                key={a.id}
                                data-testid={`activity-card-${i}`}
                                className="bg-inksurface group flex flex-col"
                            >
                                <div className="aspect-[4/3] w-full overflow-hidden bg-zinc-900 relative">
                                    {a.image_url ? (
                                        <ActivityImage src={a.image_url} partner={a.partner} />
                                    ) : (
                                        <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                                            <div className="font-graffiti text-2xl md:text-3xl text-volt leading-none">{a.partner}</div>
                                        </div>
                                    )}
                                </div>
                                <div className="p-6 md:p-8 flex flex-col flex-1">
                                    <h3 className="font-display font-bold text-white text-lg md:text-xl tracking-tight leading-tight">
                                        {title}
                                    </h3>
                                    <p className="mt-4 text-sm text-zinc-400 leading-relaxed flex-1 whitespace-pre-line">{description}</p>
                                    {impact ? (
                                        <div className="mt-6 inline-flex items-center text-xs font-mono uppercase tracking-[0.2em] text-volt border border-volt/30 px-3 py-2 w-fit">
                                            {impact}
                                        </div>
                                    ) : null}
                                    {a.external_url ? (
                                        <a
                                            href={a.external_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="mt-5 text-xs uppercase tracking-wider text-zinc-300 hover:text-volt inline-flex items-center gap-2"
                                        >
                                            {t.activities.visit} <ArrowUpRight size={14} />
                                        </a>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


// Renders wide wordmark/logos (aspect > 1.8) centered with padding, in brand colours —
// photos keep the full-bleed cover treatment with grayscale hover.
const ActivityImage = ({ src, partner }) => {
    const [isLogo, setIsLogo] = useState(false);

    if (isLogo) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-ink p-10 md:p-14">
                <img
                    src={src}
                    alt={partner}
                    data-testid="activity-image-logo"
                    className="max-w-full max-h-full object-contain"
                />
            </div>
        );
    }

    return (
        <>
            <img
                src={src}
                alt={partner}
                onLoad={(e) => {
                    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                    if (w && h && w / h > 1.8) setIsLogo(true);
                }}
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-inksurface via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                <div className="font-graffiti text-2xl md:text-3xl text-volt leading-none drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                    {partner}
                </div>
            </div>
        </>
    );
};